// apps/spa/src/components/layout/BotHealthPill.tsx
// OPS-01: top-of-header pill showing the bot's online/offline state + the
// time since the last health probe.
//
// Skeleton while loading; green dot + 'Online · last <Xs/Xm> ago' when
// data.online === true; red dot + 'Offline · last ...' when false.
//
// Polling cadence + Page Visibility pause live in useBotHealth.

import { useReducedMotion } from 'motion/react';
import { useBotHealth } from '@/hooks/useBotHealth';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export function formatAge(lastChecked: number): string {
  const ms = Math.max(0, Date.now() - lastChecked);
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}

export function BotHealthPill() {
  const { data, isLoading } = useBotHealth();
  const reduce = useReducedMotion();

  if (isLoading || !data) {
    return (
      <Skeleton
        data-testid="bot-health-skeleton"
        className="h-7 w-40 rounded-full"
      />
    );
  }

  const online = data.online;
  return (
    <div
      data-testid="bot-health-pill"
      data-state={online ? 'online' : 'offline'}
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ring-1',
        online
          ? 'bg-emerald-500/10 text-emerald-300 ring-[var(--line-strong)]'
          : 'bg-rose-500/10 text-rose-300 ring-rose-500/25',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-2 w-2 rounded-full',
          online ? 'bg-[var(--accent)]' : 'bg-rose-400',
        )}
        style={
          online && !reduce
            ? { animation: 'pulse-dot 2s ease-in-out infinite' }
            : undefined
        }
      />
      <span>
        {online ? 'Online' : 'Offline'} · last {formatAge(data.lastChecked)}
      </span>
    </div>
  );
}
